import {
  AlertTriangle,
  CheckCircle,
  Copy,
  Download,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";

interface BackupCodesProps {
  backupCodes: string[];
  onComplete: () => void;
}

export function BackupCodes({ backupCodes, onComplete }: BackupCodesProps) {
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleCopy = async () => {
    try {
      const codesText = backupCodes.join("\n");
      await navigator.clipboard.writeText(codesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error("Failed to copy backup codes:", error);
    }
  };

  const handleDownload = () => {
    const codesText = `Dashboard Gestor - Códigos de Backup 2FA\n\nGuarde estes códigos em um local seguro. Cada código só pode ser usado uma vez.\n\n${backupCodes.join("\n")}\n\nData de geração: ${new Date().toLocaleString("pt-BR")}`;

    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `backup-codes-2fa-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setDownloaded(true);
  };

  const handleComplete = () => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    onComplete();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Códigos de Backup Gerados</CardTitle>
          <p className="text-muted-foreground">
            Guarde estes códigos em um local seguro. Você precisará deles se
            perder acesso ao seu aplicativo autenticador.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Importante:</strong> Estes códigos são exibidos apenas uma
              vez. Cada código só pode ser usado uma única vez. Guarde-os em um
              local seguro e acessível.
            </AlertDescription>
          </Alert>

          {/* Backup Codes Grid */}
          <div className="bg-muted p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="bg-background p-3 rounded border text-center font-semibold"
                >
                  {code}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleCopy}
              className="flex items-center gap-2"
              disabled={copied}
            >
              {copied ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Copiado!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copiar Códigos
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={handleDownload}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {downloaded ? "Baixado!" : "Baixar como .txt"}
            </Button>
          </div>

          <Separator />

          {/* Security Tips */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">💡 Dicas de Segurança:</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Guarde os códigos em um gerenciador de senhas</li>
              <li>• Armazene uma cópia física em local seguro</li>
              <li>• Não compartilhe estes códigos com ninguém</li>
              <li>• Cada código só pode ser usado uma vez</li>
              <li>
                • Você pode gerar novos códigos a qualquer momento nas
                configurações
              </li>
            </ul>
          </div>

          <Separator />

          {/* Confirmation */}
          {!confirmed ? (
            <div className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Confirmação necessária:</strong> Certifique-se de que
                  você salvou os códigos de backup antes de continuar. Eles não
                  serão exibidos novamente.
                </AlertDescription>
              </Alert>

              <Button
                onClick={handleComplete}
                className="w-full"
                variant="outline"
              >
                Eu salvei os códigos de backup
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>
                    Autenticação de dois fatores configurada com sucesso!
                  </strong>
                  <br />
                  Você agora pode acessar o dashboard com segurança.
                </AlertDescription>
              </Alert>

              <Button onClick={handleComplete} className="w-full">
                Acessar Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
